import "@/App.css";
import { QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";

import { AuthProvider } from "@/lib/auth";
import { queryClient } from "@/lib/query";
import { ToastProvider } from "@/components/toast";
import { ErrorBoundary } from "@/components/error-boundary";
import { AppShell } from "@/components/app-shell";

import Welcome, { Index } from "@/pages/auth/welcome";
import { AdminLogin, Login } from "@/pages/auth/login";
import Register from "@/pages/auth/register";
import ForgotPassword from "@/pages/auth/forgot-password";
import Home from "@/pages/agent/home";
import Dossiers from "@/pages/agent/dossiers";
import Ranking from "@/pages/agent/ranking";
import Profile from "@/pages/agent/profile";
import Recommend from "@/pages/agent/recommend";
import DossierScreen from "@/pages/agent/dossier";
import Referral from "@/pages/agent/referral";
import Commissions from "@/pages/agent/commissions";
import Account from "@/pages/agent/account";
import RibScreen from "@/pages/agent/rib";
import Notifications from "@/pages/notifications";
import AdminHome from "@/pages/admin/home";
import AdminDossiers from "@/pages/admin/dossiers";
import AdminFinance from "@/pages/admin/finance";
import AdminAgents from "@/pages/admin/agents";
import AdminDossier from "@/pages/admin/dossier";
import AdminAgent from "@/pages/admin/agent";
import AdminRanking from "@/pages/admin/ranking";
import AdminSettings from "@/pages/admin/settings";
import AdminReferrals from "@/pages/admin/referrals";
import AdminClients from "@/pages/admin/clients";
import AdminStats from "@/pages/admin/stats";
import MasterDetail from "@/pages/admin/master";
import MasterForm from "@/pages/admin/master-form";
import Planning from "@/pages/admin/planning";
import AppointmentForm from "@/pages/admin/appointment-form";

const wrap = (el) => <ErrorBoundary>{el}</ErrorBoundary>;

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <ToastProvider>
            <BrowserRouter>
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/welcome" element={<Welcome />} />
                <Route path="/login" element={<Login />} />
                <Route path="/admin-login" element={<AdminLogin />} />
                <Route path="/register" element={<Register />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />

                <Route element={<AppShell role="agent" />}>
                  <Route path="/home" element={wrap(<Home />)} />
                  <Route path="/dossiers" element={wrap(<Dossiers />)} />
                  <Route path="/ranking" element={wrap(<Ranking />)} />
                  <Route path="/profile" element={wrap(<Profile />)} />
                  <Route path="/recommend" element={wrap(<Recommend />)} />
                  <Route path="/dossier/:id" element={wrap(<DossierScreen />)} />
                  <Route path="/referral" element={wrap(<Referral />)} />
                  <Route path="/commissions" element={wrap(<Commissions />)} />
                  <Route path="/account" element={wrap(<Account />)} />
                  <Route path="/rib" element={wrap(<RibScreen />)} />
                </Route>

                <Route element={<AppShell role="admin" />}>
                  <Route path="/admin-home" element={wrap(<AdminHome />)} />
                  <Route path="/admin-dossiers" element={wrap(<AdminDossiers />)} />
                  <Route path="/admin-finance" element={wrap(<AdminFinance />)} />
                  <Route path="/admin-agents" element={wrap(<AdminAgents />)} />
                  <Route path="/admin/dossier/:id" element={wrap(<AdminDossier />)} />
                  <Route path="/admin/agent/:id" element={wrap(<AdminAgent />)} />
                  <Route path="/admin/ranking" element={wrap(<AdminRanking />)} />
                  <Route path="/admin/settings" element={wrap(<AdminSettings />)} />
                  <Route path="/admin/referrals" element={wrap(<AdminReferrals />)} />
                  <Route path="/admin/clients" element={wrap(<AdminClients />)} />
                  <Route path="/admin/stats" element={wrap(<AdminStats />)} />
                  <Route path="/admin/master/:id" element={wrap(<MasterDetail />)} />
                  <Route path="/admin/master-form" element={wrap(<MasterForm />)} />
                  <Route path="/admin/planning" element={wrap(<Planning />)} />
                  <Route path="/admin/appointment-form" element={wrap(<AppointmentForm />)} />
                </Route>

                <Route element={<AppShell role="any" />}>
                  <Route path="/notifications" element={wrap(<Notifications />)} />
                </Route>

                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </BrowserRouter>
          </ToastProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
